import React, { useState, useEffect } from "react";
import { FormControl, Switch, Grid2, TextField, Box, Button, FormLabel, FormControlLabel, RadioGroup, Radio, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

export default function ConnectionAPIForm({ connection, onDataChange, setShowSubmit, validationError, setValidationError, editMode }) {
    const [data, setData] = useState(JSON.parse(connection.data || '{}'));
    const [tokenForm, setTokenForm] = useState(false);
    const [editBody, setEditBody] = useState(false);
    const [editHeaders, setEditHeaders] = useState(false);
    const [headers, setHeaders] = useState({});
    const [body, setBody] = useState({});
    const [fieldsToEncrypt, setFieldsToEncrypt] = useState([]);
    const [loadingEncrypt, setLoadingEncrypt] = useState(false);
    const [editToken, setEditToken] = useState(false);

    const id = connection.id || "new";

    async function encryptValue(value) {
        try {
            setLoadingEncrypt(true);
            const response = await fetch('/api/encryption/encrypt', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: value }),
            });
            if (response.ok) {
                const result = await response.json();
                return result.encryptedText;
            } else {
                console.error('Encryption failed');
                return value; // Return original value if encryption fails
            }
        } catch (error) {
            console.error('Error during encryption:', error);
            return value; // Return original value on error
        } finally {
            setLoadingEncrypt(false);
        }
    }

    async function decryptValue(value) {
        try {
            setLoadingEncrypt(true);
            const response = await fetch('/api/encryption/decrypt', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: value }),
            });
            if (response.ok) {
                const result = await response.json();
                return result.decryptedText;
            } else {
                console.error('Decryption failed');
                return value; // Return original value if decryption fails
            }
        } catch (error) {
            console.error('Error during decryption:', error);
            return value; // Return original value on error
        } finally {
            setLoadingEncrypt(false);
        }
    }

    useEffect(() => {
        let newData = JSON.parse(connection.data || '{}');
        setData(newData);
        setTokenForm(newData.token ? true : false);
        setFieldsToEncrypt(newData.auth?.decrypt || []);
        if (newData.auth?.body) {
            const newBody = {};
            Object.keys(newData.auth.body).forEach(key => {
                newBody[key] = { key: key, value: newData.auth.body[key] }
            });
            setBody(newBody);
        } else {
            setBody({});
        }

        if (newData.auth?.headers) {
            const newHeaders = {};
            Object.keys(newData.auth.headers).forEach(key => {
                newHeaders[key] = { key: key, value: newData.auth.headers[key] }
            });
            setHeaders(newHeaders);
        } else {
            setHeaders({});
        }

    }, [connection.data]);

    const handleTokenFormChange = (event) => {
        const newTokenForm = event.target.checked;
        setTokenForm(newTokenForm);
    }

    const handleBaseAddressChange = (event) => {
        const newBaseAddress = event.target.value;
        setData(prev => ({ ...prev, baseAddress: newBaseAddress }));
        onDataChange && onDataChange({ ...data, baseAddress: newBaseAddress });
    }

    const handleTokenTypeChange = (event) => {
        const newTokenType = event.target.value;
        setData(prev => ({ ...prev, tokenType: newTokenType }));
        onDataChange && onDataChange({ ...data, tokenType: newTokenType });
    }

    const handleTokenChange = (event) => {
        const newToken = event.target.value;
        setData(prev => ({ ...prev, token: newToken }));
        onDataChange && onDataChange({ ...data, token: newToken });
    }

    const handleRouteChange = (event) => {
        const newRoute = event.target.value;
        setData(prev => {
            const newData = { ...prev };
            if (!newData.auth) newData.auth = {};
            newData.auth.route = newRoute;
            return newData;
        });
        onDataChange && onDataChange({ ...data, auth: { ...data.auth, route: newRoute } });
    }

    const handleTokenPathChange = (event) => {
        const newTokenPath = event.target.value;
        setData(prev => {
            const newData = { ...prev };
            if (!newData.auth) newData.auth = {};
            newData.auth.tokenPath = newTokenPath;
            return newData;
        });
        onDataChange && onDataChange({ ...data, auth: { ...data.auth, tokenPath: newTokenPath } });
    }

    const handleBaseAddressPathChange = (event) => {
        const newBaseAddressPath = event.target.value;
        setData(prev => {
            const newData = { ...prev };
            if (!newData.auth) newData.auth = {};
            newData.auth.baseAddressPath = newBaseAddressPath;
            return newData;
        });
        onDataChange && onDataChange({ ...data, auth: { ...data.auth, baseAddressPath: newBaseAddressPath } });
    }

    const handleBodyKeyChange = (key, newKey) => {
        setBody(prev => {
            const newBody = { ...prev };
            newBody[key].key = newKey;
            return newBody;
        });
    }

    const handleHeaderKeyChange = (key, newKey) => {
        setHeaders(prev => {
            const newHeaders = { ...prev };
            newHeaders[key].key = newKey;
            return newHeaders;
        });
    }

    const handleBodyValueChange = (key, newValue) => {
        setBody(prev => {
            const newBody = { ...prev };
            newBody[key].value = newValue;
            return newBody;
        });
    }

    const handleHeaderValueChange = (key, newValue) => {
        setHeaders(prev => {
            const newHeaders = { ...prev };
            newHeaders[key].value = newValue;
            return newHeaders;
        });
    }

    const handleEditBody = (val) => {
        if (!val) {
            if (data.auth?.body) {
                const newBody = {};
                Object.keys(data.auth.body).forEach(key => {
                    newBody[key] = { key: key, value: data.auth.body[key] }
                });
                setBody(newBody);
            } else {
                setBody({});
            }
        }
        setEditBody(val);
        setShowSubmit && setShowSubmit(!val);
    }

    const handleEditHeaders = (val) => {
        if (!val) {
            if (data.auth?.headers) {
                const newHeaders = {};
                Object.keys(data.auth.headers).forEach(key => {
                    newHeaders[key] = { key: key, value: data.auth.headers[key] }
                });
                setHeaders(newHeaders);
            } else {
                setHeaders({});
            }
        }
        setEditHeaders(val);
        setShowSubmit && setShowSubmit(!val);
    }

    const handleAcceptBody = () => {
        let newBody = {};
        Object.keys(body).map(k => {
            if (body[k].key?.trim() === '') {
                delete body[k];
            }
            newBody[body[k].key] = body[k].value;
        });
        setData(prev => {
            const newData = { ...prev };
            if (!newData.auth) newData.auth = {};
            newData.auth.body = newBody;
            return newData;
        });
        onDataChange && onDataChange({ ...data, auth: { ...data.auth, body: newBody } });
        setEditBody(false);
        setShowSubmit && setShowSubmit(true);
    }

    const handleAcceptHeaders = () => {
        let newHeaders = {};
        Object.keys(headers).map(k => {
            if (headers[k].key?.trim() === '') {
                delete headers[k];
            }
            newHeaders[headers[k].key] = headers[k].value;
        });
        setData(prev => {
            const newData = { ...prev };
            if (!newData.auth) newData.auth = {};
            newData.auth.headers = newHeaders;
            return newData;
        });
        onDataChange && onDataChange({ ...data, auth: { ...data.auth, headers: newHeaders } });
        setEditHeaders(false);
        setShowSubmit && setShowSubmit(true);
    }

    const handleRequestTypeChange = (event) => {
        const newRequestType = parseInt(event.target.value);
        setData(prev => {
            const newData = { ...prev };
            if (!newData.auth) newData.auth = {};
            newData.auth.requestType = newRequestType;
            return newData;
        });
        onDataChange && onDataChange({ ...data, auth: { ...data.auth, requestType: newRequestType } });
    }

    const handleEncryptCheckboxChange = async (key, val) => {
        let encVal = body[key].value;
        if (val) {
            encVal = await encryptValue(body[key].value);
            handleBodyValueChange(key, encVal);
            setFieldsToEncrypt(prev => prev.includes(key) ? prev : [...prev, key]);
        } else {
            encVal = await decryptValue(body[key].value);
            handleBodyValueChange(key, encVal);
            setFieldsToEncrypt(prev => prev.filter(item => item !== key));
        }

        let newBody = {};
        Object.keys(body).map(k => {
            if (body[k].key?.trim() === '') {
                delete body[k];
            }
            newBody[body[k].key] = body[k].value;
            if (k == key) {
                newBody[body[k].key] = encVal;
            }
        });

        let newFieldsToEncrypt = [...fieldsToEncrypt];
        if (val) {
            if (!newFieldsToEncrypt.includes(key)) {
                newFieldsToEncrypt.push(key);
            }
        } else {
            newFieldsToEncrypt = newFieldsToEncrypt.filter(item => item !== key);
        }
        onDataChange && onDataChange({ ...data, auth: { ...data.auth, body: newBody, decrypt: newFieldsToEncrypt } });
    }

    const handleEncryptToken = async (enc) => {
        let val = data.token || '';
        if (!enc) {
            val = await encryptValue(data.token);
        } else {
            val = await decryptValue(data.token);
        }
        onDataChange && onDataChange({ ...data, token: val });
        setEditToken(enc);
        setShowSubmit && setShowSubmit(!enc);
    }


    return (
        <div>
            <h4>API Connection Details</h4>
            <FormControl fullWidth margin="normal">
                <TextField
                    label="Base Address"
                    id={`connection-base-address-${id}`}
                    name={`connection-base-address-${id}`}
                    type="text"
                    defaultValue={data.baseAddress ?? ''}
                    onChange={handleBaseAddressChange}
                    variant={editMode ? "outlined" : "standard"}
                    InputProps={{
                        readOnly: !editMode
                    }}
                />
            </FormControl>
            <FormControl fullWidth margin="normal">
                <TextField
                    label="Token Type"
                    id={`connection-token-type-${id}`}
                    name={`connection-token-type-${id}`}
                    type="text"
                    defaultValue={data.tokenType ?? 'Bearer'}
                    onChange={handleTokenTypeChange}
                    variant={editMode ? "outlined" : "standard"}
                    InputProps={{
                        readOnly: !editMode
                    }}
                />
            </FormControl>
            <FormControl fullWidth margin="normal" sx={{ display: 'inline-block' }}>
                Auth <Switch checked={tokenForm} onChange={handleTokenFormChange} disabled={!editMode} /> Token
            </FormControl>
            {tokenForm && (
                <Grid2 container columns={16} spacing={1}>
                    <Grid2 size={14}>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                disabled={!editToken || loadingEncrypt}
                                label="Token"
                                size="small"
                                id={`connection-token-${id}`}
                                name={`connection-token-${id}`}
                                type="text"
                                value={data.token ?? ''}
                                onChange={handleTokenChange}
                                variant={editMode ? "outlined" : "standard"}
                                InputProps={{
                                    readOnly: !editMode
                                }}
                            />
                        </FormControl>
                    </Grid2>
                    <Grid2 size={2}>
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Button variant="outlined" color="error" size="large" disabled={!editMode}
                                onClick={() => handleEncryptToken(!editToken)}
                            >
                                {editToken ? <LockOpenIcon /> : <LockIcon />}
                            </Button>
                        </Box>
                    </Grid2>
                </Grid2>
            )}
            {!tokenForm && (
                <>
                    <div>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Route"
                                id={`connection-route-${id}`}
                                name={`connection-route-${id}`}
                                type="text"
                                defaultValue={data.auth?.route ?? ''}
                                onChange={handleRouteChange}
                                variant={editMode ? "outlined" : "standard"}
                                InputProps={{
                                    readOnly: !editMode
                                }}
                            />
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Token Dot Path (from response)"
                                id={`connection-token-dot-path-${id}`}
                                name={`connection-token-dot-path-${id}`}
                                type="text"
                                defaultValue={data.auth?.tokenPath ?? ''}
                                onChange={handleTokenPathChange}
                                variant={editMode ? "outlined" : "standard"}
                                InputProps={{
                                    readOnly: !editMode
                                }}
                            />
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Base Address Dot Path (from response)"
                                id={`connection-base-address-dot-path-${id}`}
                                name={`connection-base-address-dot-path-${id}`}
                                type="text"
                                defaultValue={data.auth?.baseAddressPath ?? ''}
                                onChange={handleBaseAddressPathChange}
                                variant={editMode ? "outlined" : "standard"}
                                InputProps={{
                                    readOnly: !editMode
                                }}
                            />
                        </FormControl>
                    </div>
                    <div>
                        <h5>Body</h5>
                        <FormControl fullWidth margin="normal">
                            <FormLabel id="connection-api-request-type">Request Type</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby="connection-api-request-type"
                                name="connection-api-request-type"
                                value={data.auth?.requestType ?? 0}
                                onChange={handleRequestTypeChange}
                            >
                                <FormControlLabel value={0} control={<Radio disabled={!editMode} />} label="form-data" />
                                <FormControlLabel value={1} control={<Radio disabled={!editMode} />} label="x-www-form-urlencode" />
                                <FormControlLabel value={2} control={<Radio disabled={!editMode} />} label="json" />
                            </RadioGroup>
                        </FormControl>
                        <Button variant="outlined" disabled={!editMode} onClick={() => handleEditBody(!editBody)}>{editBody ? 'Cancel' : 'Edit Body'}</Button>
                        {editBody && (
                            <Button color="success" sx={{ ml: 2 }} variant="contained" onClick={handleAcceptBody}>Accept</Button>
                        )}
                        <Grid2 container columns={16} spacing={1}>
                            <Grid2 size={4}>
                                <strong>Key</strong>
                            </Grid2>
                            <Grid2 size={10}>
                                <strong>Value</strong>
                            </Grid2>
                            <Grid2 size={1}>
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <LockIcon />
                                </Box>
                            </Grid2>
                            <Grid2 size={1}>
                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                    <DeleteIcon />
                                </Box>
                            </Grid2>
                        </Grid2>
                        {Object.keys(body).map((key) => (
                            <Grid2 container columns={16} key={key} spacing={1}>
                                <Grid2 size={4}>
                                    <FormControl fullWidth margin="normal">
                                        <TextField
                                            size="small"
                                            id={`connection-body-label-${key}-${id}`}
                                            name={`connection-body-label-${key}-${id}`}
                                            type="text"
                                            value={body[key].key ?? ''}
                                            disabled={!editBody || loadingEncrypt}
                                            onChange={(e) => handleBodyKeyChange(key, e.target.value)}
                                            variant={editMode ? "outlined" : "standard"}
                                            InputProps={{
                                                readOnly: !editMode
                                            }}
                                        />
                                    </FormControl>
                                </Grid2>
                                <Grid2 size={10}>
                                    <FormControl fullWidth margin="normal">
                                        <TextField
                                            size="small"
                                            id={`connection-body-${key}-${id}`}
                                            name={`connection-body-${key}-${id}`}
                                            type="text"
                                            value={body[key].value ?? ''}
                                            disabled={!editBody || (fieldsToEncrypt.includes(key)) || loadingEncrypt}
                                            onChange={(e) => handleBodyValueChange(key, e.target.value)}
                                            variant={editMode ? "outlined" : "standard"}
                                            InputProps={{
                                                readOnly: !editMode
                                            }}
                                        />
                                    </FormControl>
                                </Grid2>
                                <Grid2 size={1}>
                                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                        <IconButton disabled={editBody || loadingEncrypt || !editMode} variant="outlined" color="primary" size="small" >
                                            {fieldsToEncrypt.includes(key) ? (
                                                <CheckBoxIcon onClick={() => handleEncryptCheckboxChange(key, false)} />
                                            ) : (
                                                <CheckBoxOutlineBlankIcon onClick={() => handleEncryptCheckboxChange(key, true)} />
                                            )}
                                        </IconButton>
                                    </Box>
                                </Grid2>
                                <Grid2 size={1}>
                                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                        <IconButton disabled={!editBody || !editMode} variant="outlined" color="error" size="small" onClick={() => {
                                            setBody(prev => {
                                                const newBody = { ...prev };
                                                delete newBody[key];
                                                return newBody;
                                            });
                                        }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Grid2>
                            </Grid2>
                        ))}
                        <div style={{ marginTop: 10 }}>
                            {editBody && (
                                <Button variant="outlined" disabled={!editMode} onClick={() => {
                                    const newKey = `key${Object.keys(body).length + 1}`;
                                    setBody(prev => ({ ...prev, [newKey]: { key: '', value: '' } }));
                                }}>Add Body Key</Button>
                            )}
                        </div>
                    </div>
                </>
            )}
            <div>
                <h5>Headers</h5>
                <Button variant="outlined" disabled={!editMode} onClick={() => handleEditHeaders(!editHeaders)}>{editHeaders ? 'Cancel' : 'Edit Headers'}</Button>
                {editHeaders && (
                    <Button color="success" sx={{ ml: 2 }} variant="contained" onClick={handleAcceptHeaders}>Accept</Button>
                )}
                {Object.keys(headers).map((key) => (
                    <Grid2 container columns={16} key={key} spacing={1}>
                        <Grid2 size={4}>
                            <FormControl fullWidth margin="normal">
                                <TextField
                                    size="small"
                                    id={`connection-header-label-${key}-${id}`}
                                    name={`connection-header-label-${key}-${id}`}
                                    type="text"
                                    value={headers[key].key ?? ''}
                                    disabled={!editHeaders}
                                    onChange={(e) => handleHeaderKeyChange(key, e.target.value)}
                                    variant={editMode ? "outlined" : "standard"}
                                    InputProps={{
                                        readOnly: !editMode
                                    }}
                                />
                            </FormControl>
                        </Grid2>
                        <Grid2 size={11}>
                            <FormControl fullWidth margin="normal">
                                <TextField
                                    size="small"
                                    id={`connection-header-${key}-${id}`}
                                    name={`connection-header-${key}-${id}`}
                                    type="text"
                                    value={headers[key].value ?? ''}
                                    disabled={!editHeaders}
                                    onChange={(e) => handleHeaderValueChange(key, e.target.value)}
                                    variant={editMode ? "outlined" : "standard"}
                                    InputProps={{
                                        readOnly: !editMode
                                    }}
                                />
                            </FormControl>
                        </Grid2>
                        <Grid2 size={1}>
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                <IconButton disabled={!editHeaders || !editMode} variant="outlined" color="error" size="small" onClick={() => {
                                    setHeaders(prev => {
                                        const newHeaders = { ...prev };
                                        delete newHeaders[key];
                                        return newHeaders;
                                    });
                                }}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Grid2>
                    </Grid2>
                ))}
                <div style={{ marginTop: 10 }}>
                    {editHeaders && (
                        <Button variant="outlined" disabled={!editMode} onClick={() => {
                            const newKey = `key${Object.keys(headers).length + 1}`;
                            setHeaders(prev => ({ ...prev, [newKey]: { key: '', value: '' } }));
                        }}>Add Header Key</Button>
                    )}
                </div>
            </div>
        </div>
    );
}
